import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Lock, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!username.trim() || !password.trim()) {
      setError(t('fillAllFields') || "Por favor completa todos los campos");
      return;
    }

    setIsLoading(true);
    try {
      await login(username.trim(), password);
      toast.success(t('loginSuccess') || "¡Bienvenido!");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === "string" 
        ? detail 
        : (t('invalidCredentials') || "Usuario o contraseña incorrectos");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img src="/mf-logo.png" alt="MFcreativemanager" className="w-10 h-10 object-contain" />
            <span 
              className="font-bold text-xl text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('appName')}
            </span>
          </div>
          <LanguageSwitcher variant="minimal" />
        </div>
      </header>

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
            {/* Title */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-pink-600 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 
                className="text-2xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('adminLogin') || 'Acceso Administrador'}
              </h1>
              <p className="text-slate-500 mt-2">
                {t('loginToManage') || 'Inicia sesión para gestionar tus álbumes'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">
                  {t('username') || 'Usuario'}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('enterUsername') || 'Ingresa tu usuario'}
                    className="h-12 pl-11 rounded-xl"
                    data-testid="login-username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">
                  {t('password') || 'Contraseña'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('enterPassword') || 'Ingresa tu contraseña'}
                    className="h-12 pl-11 pr-11 rounded-xl"
                    data-testid="login-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-semibold"
                data-testid="login-submit"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('loggingIn') || 'Iniciando sesión...'}
                  </div>
                ) : (
                  t('login') || 'Iniciar Sesión'
                )}
              </Button>
            </form>
          </div>

          {/* Back to home */}
          <p className="text-center text-slate-500 mt-6">
            <button
              onClick={() => navigate("/")}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              ← {t('backToHome') || 'Volver al inicio'}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
